import React, { useState } from 'react';

import { Download, X } from 'lucide-react';
import { queryCache, useMutation } from 'react-query';
import { Toast } from 'src/common/components';

import { importCohortMembers } from '../api';
import {
  CSV_REJECTED_PREVIEW_LIMIT,
  CSV_REJECTION_REASON_LABEL,
  RECIPIENT_COHORT_MEMBERS_QUERY_KEY,
  RECIPIENT_COHORTS_QUERY_KEY,
} from '../constants';
import { CsvImportResult, RecipientCohort } from '../types';
import {
  buildRejectedRowsCsv,
  downloadCsv,
  getCohortErrorMessage,
  invalidateCohortHistory,
} from '../utils';

type CsvImportModalProps = {
  cohort: RecipientCohort;
  onClose: () => void;
};

type ReviewedFile = { file: File; result: CsvImportResult };

const CsvImportModal: React.FC<CsvImportModalProps> = ({ cohort, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [reviewed, setReviewed] = useState<ReviewedFile | null>(null);

  const [review, { isLoading: isReviewing }] = useMutation(
    (selected: File) => importCohortMembers(cohort.id, selected, true),
    {
      onSuccess: (res, selected) => setReviewed({ file: selected, result: res.data }),
      onError: (error) => {
        Toast.error(getCohortErrorMessage(error, 'No se pudo revisar el archivo.'));
      },
    }
  );

  const [confirm, { isLoading: isImporting }] = useMutation(
    (selected: File) => importCohortMembers(cohort.id, selected, false),
    {
      onSuccess: (res) => {
        void queryCache.invalidateQueries([RECIPIENT_COHORT_MEMBERS_QUERY_KEY, cohort.id]);
        void queryCache.invalidateQueries(RECIPIENT_COHORTS_QUERY_KEY);
        invalidateCohortHistory('recipient', cohort.id);
        Toast.success(
          `Importación lista: ${res.data.added_count} persona${
            res.data.added_count !== 1 ? 's' : ''
          } añadida${res.data.added_count !== 1 ? 's' : ''}.`
        );
        onClose();
      },
      onError: (error) => {
        Toast.error(getCohortErrorMessage(error, 'No se pudo importar el archivo.'));
      },
    }
  );

  // A dry run can finish after the user picked another file; only a summary that belongs to the
  // current file may be shown and confirmed.
  const summary = reviewed && reviewed.file === file ? reviewed.result : null;

  const chooseFile = (selected: File | null): void => {
    setFile(selected);
    setReviewed(null);
  };

  const visibleRejected = summary?.rejected.slice(0, CSV_REJECTED_PREVIEW_LIMIT) ?? [];
  const hiddenRejectedCount = (summary?.rejected.length ?? 0) - visibleRejected.length;

  return (
    <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center">
      <div className="rounded-2xl w-full max-w-lg mx-4 overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-bold text-gray-900">Importar CSV a {cohort.name}</h3>
          <button type="button" className="btn btn-ghost p-1 text-gray-400" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              Archivo CSV
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="block w-full mt-1 text-sm"
              onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
            />
            <span className="block mt-1 text-xs text-gray-400">
              Una columna «email» (o una sola columna sin encabezado). Máximo 1 MB y 5000 filas.
            </span>
          </label>

          {summary && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-800">
                Entran {summary.added_count} · Ya estaban {summary.already_member_count} ·
                Rechazadas {summary.rejected_count}
              </p>

              {summary.rejected.length > 0 && (
                <>
                  <div className="max-h-52 overflow-y-auto border border-gray-200 rounded">
                    {visibleRejected.map((row) => (
                      <div
                        key={`${row.row}-${row.email}`}
                        className="last:border-0 flex items-center gap-2 px-3 py-1.5 text-xs border-b"
                      >
                        <span className="w-12 text-gray-400">Fila {row.row}</span>
                        <span className="flex-1 text-gray-700 truncate">
                          {row.email || '(vacío)'}
                        </span>
                        <span className="text-red-600">
                          {CSV_REJECTION_REASON_LABEL[row.reason]}
                        </span>
                      </div>
                    ))}
                  </div>
                  {hiddenRejectedCount > 0 && (
                    <p className="text-xs text-gray-400">
                      Y {hiddenRejectedCount} más. Descarga las rechazadas para verlas todas.
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost inline-flex items-center gap-1 px-3 py-1 text-xs text-blue-700 border border-gray-200 rounded-full"
                    onClick={() =>
                      downloadCsv('filas-rechazadas.csv', buildRejectedRowsCsv(summary.rejected))
                    }
                  >
                    <Download size={12} />
                    Descargar rechazadas
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          {summary ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={isImporting || summary.added_count === 0}
              onClick={() => file && confirm(file)}
            >
              {isImporting ? 'Importando…' : `Confirmar importación (${summary.added_count})`}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!file || isReviewing}
              onClick={() => file && review(file)}
            >
              {isReviewing ? 'Revisando…' : 'Revisar archivo'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
